/**
 * ReliabilityBayGrid
 *
 * One tile per configured bay, in configuration order. Tiles distinguish bays with their
 * own problem from bays only affected by a site-wide reason, and show current activity:
 * shots tracked in the last 3 minutes, with or without Go in use. Selecting a tile shows
 * its details.
 *
 * The grid starts collapsed when every tile would look the same: all bays OK, or all
 * bays affected only by the site-wide issue (already explained above the grid). It never
 * starts collapsed while a bay reported offline is tracking shots.
 */
import { useState, type CSSProperties } from 'react';
import type {
  BayActivity,
  BayReliability,
  SiteReliabilityHint,
} from '../../models/reliabilityModels';
import type { SuperBay } from '../../models/domainModels';
import {
  activityRecency,
  assessBayActivity,
  bayAccessibleLabel,
  bayLabel,
  bayShortLabel,
  classifyBayConcern,
  concernTooltip,
  describeActivitySummary,
  describeBaySummary,
  describeTrackingMismatch,
  lastShotTrackedSentence,
  recencyTooltip,
  sessionsTooltip,
  summarizeActivity,
  summarizeBays,
} from '../../utils/reliabilityPresentation';
import { BayReliabilityDetail } from './BayReliabilityDetail';

interface ReliabilityBayGridProps {
  readonly bays: readonly BayReliability[];
  readonly site: SiteReliabilityHint;
  readonly superBays: readonly SuperBay[];
  /** When the assessment was made; the reference time for current activity. */
  readonly evaluatedAt: string;
}

const DETAIL_ID = 'reliability-bay-detail';
const GRID_ID = 'reliability-bay-grid';

/**
 * Bar length and colour for the time since the last tracked shot. The colour mixes from
 * grey toward the primary blue with recency, so a full bar reads as "tracking now".
 */
function recencyStyle(recency: number): CSSProperties {
  const percent = `${Math.round(recency * 1000) / 10}%`;
  return { width: percent, ['--recency' as string]: percent };
}

function tileClassName(
  concern: string,
  activity: BayActivity,
  isMismatch: boolean,
  isSelected: boolean
): string {
  return [
    'reliability-bay',
    `reliability-bay--${concern}`,
    activity !== 'idle' ? `reliability-bay--${activity}` : null,
    isMismatch ? 'reliability-bay--mismatch' : null,
    isSelected ? 'reliability-bay--selected' : null,
  ]
    .filter((name): name is string => name !== null)
    .join(' ');
}

export function ReliabilityBayGrid({
  bays,
  site,
  superBays,
  evaluatedAt,
}: ReliabilityBayGridProps): JSX.Element {
  const reference = new Date(evaluatedAt);
  const summary = summarizeBays(bays, site);
  const activitySummary = summarizeActivity(bays, reference);
  const activityText = describeActivitySummary(activitySummary);
  const isUniform =
    summary.total > 0 &&
    activitySummary.mismatched === 0 &&
    (summary.ok === summary.total || summary.siteWide === summary.total);

  const [isExpanded, setIsExpanded] = useState(!isUniform);
  const [selectedBayId, setSelectedBayId] = useState<string | null>(null);

  // Resolve against the latest poll so the detail updates and disappears with the bay
  const selectedBay = bays.find((bay) => bay.bayId === selectedBayId);

  const toggleSelection = (bayId: string): void =>
    setSelectedBayId((current) => (current === bayId ? null : bayId));

  return (
    <div className="reliability-bays">
      <div className="reliability-bays__header">
        <h4 className="reliability-section-title">Bays</h4>
        <p className="reliability-bays__summary">{describeBaySummary(summary)}</p>
        {isUniform && (
          <button
            type="button"
            className="reliability-bays__toggle"
            aria-expanded={isExpanded}
            aria-controls={GRID_ID}
            onClick={() => setIsExpanded((expanded) => !expanded)}
          >
            {isExpanded ? 'Hide bays' : 'Show bays'}
          </button>
        )}
      </div>
      {activityText && <p className="reliability-bays__activity">{activityText}</p>}

      {isExpanded && summary.total > 0 && (
        <>
          <ul id={GRID_ID} className="reliability-bays__grid">
            {bays.map((bay) => {
              const concern = classifyBayConcern(bay, site);
              const activity = assessBayActivity(bay, reference);
              const mismatch = describeTrackingMismatch(bay, activity);
              const isMismatch = mismatch !== null;
              const recency = activityRecency(bay.lastActivityAt, reference);
              const isSelected = bay.bayId === selectedBayId;
              return (
                <li key={bay.bayId}>
                  <button
                    type="button"
                    className={tileClassName(concern, activity, isMismatch, isSelected)}
                    aria-pressed={isSelected}
                    aria-controls={isSelected ? DETAIL_ID : undefined}
                    aria-label={bayAccessibleLabel(
                      bayLabel(bay.bayId, superBays),
                      bay,
                      activity,
                      lastShotTrackedSentence(bay.lastActivityAt, reference)
                    )}
                    onClick={() => toggleSelection(bay.bayId)}
                  >
                    {/* Tooltips sit on the aria-hidden parts, not the tile, so
                        screen readers only hear the tile's aria-label */}
                    <span
                      className="reliability-dot"
                      aria-hidden="true"
                      title={concernTooltip(bay, site)}
                    />
                    <span className="reliability-bay__label" aria-hidden="true">
                      {bayShortLabel(bay.bayId, superBays)}
                    </span>
                    {bay.activeSessions !== undefined && bay.activeSessions > 0 && (
                      <span
                        className="reliability-bay__sessions"
                        aria-hidden="true"
                        title={sessionsTooltip(bay.activeSessions, activity)}
                      >
                        {bay.activeSessions}
                      </span>
                    )}
                    {recency > 0 && bay.lastActivityAt !== undefined && (
                      <span
                        className="reliability-bay__recency"
                        aria-hidden="true"
                        title={recencyTooltip(bay.lastActivityAt, reference)}
                      >
                        <span className="reliability-bay__recency-track">
                          <span
                            className="reliability-bay__recency-fill"
                            style={recencyStyle(recency)}
                          />
                        </span>
                      </span>
                    )}
                    {mismatch && (
                      <span className="reliability-bay__flag" aria-hidden="true" title={mismatch}>
                        !
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>

          {selectedBay ? (
            <BayReliabilityDetail
              id={DETAIL_ID}
              label={bayLabel(selectedBay.bayId, superBays)}
              bay={selectedBay}
              site={site}
              activity={assessBayActivity(selectedBay, reference)}
            />
          ) : (
            <p className="reliability-bays__hint">Select a bay for details.</p>
          )}

          <ul className="reliability-legend" aria-label="Legend">
            <li className="reliability-legend__item reliability-legend__item--ok">
              <span className="reliability-dot" aria-hidden="true" />OK
            </li>
            <li className="reliability-legend__item reliability-legend__item--site-wide">
              <span className="reliability-dot" aria-hidden="true" />Site-wide issue only
            </li>
            <li className="reliability-legend__item reliability-legend__item--bay-specific">
              <span className="reliability-dot" aria-hidden="true" />Bay-specific issue
            </li>
            <li className="reliability-legend__item reliability-legend__item--unknown">
              <span className="reliability-dot" aria-hidden="true" />Unknown (not confirmed)
            </li>
            <li className="reliability-legend__item reliability-legend__item--activity">
              <span className="reliability-legend__recency" aria-hidden="true">
                <span className="reliability-legend__recency-fill" />
              </span>
              Time since last shot: full = last 3 min, half = 1 h, none = 7+ days
            </li>
            <li className="reliability-legend__item">
              <span className="reliability-legend__sessions" aria-hidden="true">2</span>Active Go
              sessions (filled = Go in use)
            </li>
            <li className="reliability-legend__item">
              <span className="reliability-legend__flag" aria-hidden="true">!</span>Reported
              offline but tracking shots
            </li>
          </ul>
        </>
      )}
    </div>
  );
}
